import type { IntlShape } from 'react-intl'

/**
 * Explains why a placeholder flight shows "Likely" dates. Kept as an explicit
 * if/else list of known likely/plausible offset pairs so the copy stays easy to
 * read and extend without a lookup table abstraction.
 */
export default function getLikelyArrivalDateExplanation(
  likelyOffset: number | undefined,
  plausibleOffsets: ReadonlyArray<number> | undefined,
  intl: IntlShape,
): string {
  const fallbackExplanation = intl.formatMessage({
    id: 'mchgHtPGXjuvv9w0zZ8J',
    defaultMessage: 'We’ve estimated this arrival date. Depending on the flight schedule, it may fall on a different calendar day.',
    description: 'Fallback tooltip explaining that a likely placeholder flight arrival date is an estimate that can vary by schedule',
  })

  if (likelyOffset === undefined || !plausibleOffsets || plausibleOffsets.length === 0) {
    return fallbackExplanation
  }

  const plausibleKey = [...plausibleOffsets].sort((a, b) => a - b).join(',')

  if (likelyOffset === 0 && plausibleKey === '0,1') {
    return intl.formatMessage({
      id: 'MSrINNgsLMa9PEr3uOzl',
      defaultMessage: 'We’ve assumed same calendar day arrival for this flight. Note next calendar day is also possible for some flight schedules.',
      description: 'Tooltip explaining a likely same-day flight arrival when next-day arrival is also plausible',
    })
  }

  if (likelyOffset === 1 && plausibleKey === '0,1') {
    return intl.formatMessage({
      id: 'N6ViVPjgchJ5pZ0cUk92',
      defaultMessage: 'We’ve assumed next calendar day arrival for this flight. Note same calendar day is also possible for some flight schedules.',
      description: 'Tooltip explaining a likely next-day flight arrival when same-day arrival is also plausible',
    })
  }

  if (likelyOffset === -1 && plausibleKey === '-1,0') {
    return intl.formatMessage({
      id: '7zrgfn7lPQvrjetm_Nfi',
      defaultMessage: 'We’ve assumed previous calendar day arrival for this flight (it crosses the date line). Note same calendar day is also possible for some flight schedules.',
      description: 'Tooltip explaining a likely previous-day date-line flight arrival when same-day arrival is also plausible',
    })
  }

  if (likelyOffset === 0 && plausibleKey === '-1,0') {
    return intl.formatMessage({
      id: 'khtEqd1FWacQ-DC-18It',
      defaultMessage: 'We’ve assumed same calendar day arrival for this flight. Note previous calendar day is also possible for some flight schedules (it crosses the date line).',
      description: 'Tooltip explaining a likely same-day flight arrival when previous-day date-line arrival is also plausible',
    })
  }

  if (likelyOffset === 2 && plausibleKey === '1,2') {
    return intl.formatMessage({
      id: 'b9WwTXgf4EQN4sHB7e1C',
      defaultMessage: 'We’ve assumed arrival two calendar days later for this flight. Note next calendar day is also possible for some flight schedules.',
      description: 'Tooltip explaining a likely two-day flight arrival when next-day arrival is also plausible',
    })
  }

  if (likelyOffset === 1 && plausibleKey === '1,2') {
    return intl.formatMessage({
      id: 'ylWb4jr2ZlhFdgqa8Xkt',
      defaultMessage: 'We’ve assumed next calendar day arrival for this flight. Note arrival two calendar days later is also possible for some flight schedules.',
      description: 'Tooltip explaining a likely next-day flight arrival when two-day arrival is also plausible',
    })
  }

  return fallbackExplanation
}
